import type { QueryKey } from '@tanstack/vue-query';
import { useInfiniteQuery } from '@tanstack/vue-query';
import { sub } from 'date-fns';
import { computed, ComputedRef } from 'vue';
import type { ProjectRegistrationApiResponse } from './project.types';
import { createQueryParams } from '/@utilities/api';
import { dateToString } from '/@utilities/date';
import http from '/@utilities/http';
import { date } from '/@utilities/intl';

type GetRegistrationsParams = {
  fromDate: Date;
  toDate: Date;
};

type Registration = {
  userName: string;
  hours: number;
  date: string;
  id: number;
};

function getNextPageQueryParams(previousParams: GetRegistrationsParams) {
  return {
    fromDate: sub(previousParams.fromDate, { months: 1 }),
    toDate: sub(previousParams.toDate, { months: 1 }),
  };
}

export function useRegistrationsQuery(projectId: number) {
  const initialParams: GetRegistrationsParams = {
    fromDate: sub(new Date(), { months: 1 }),
    toDate: new Date(),
  };

  const fetchRegistrations = async ({ fromDate, toDate }: GetRegistrationsParams) => {
    return http
      .get(
        `/projects/${projectId}/registrations/?${createQueryParams(
          new Map([
            ['fromDate', dateToString(fromDate)],
            ['toDate', dateToString(toDate)],
          ]),
        )}`,
      )
      .then((response) => response.data);
  };

  const { data, fetchNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery<
    ProjectRegistrationApiResponse[],
    Error,
    { pages: ProjectRegistrationApiResponse[][]; pageParams: GetRegistrationsParams[] },
    QueryKey,
    GetRegistrationsParams
  >({
    queryFn: ({ pageParam }) => fetchRegistrations(pageParam),
    queryKey: ['registrations', { ...initialParams }],
    initialPageParam: initialParams,

    getNextPageParam: (_lastPage, _allPages, lastPageParam) =>
      getNextPageQueryParams(lastPageParam),
  });

  const transformedRegistrations: ComputedRef<Registration[]> = computed(() =>
    data.value
      ? data.value?.pages.flat().map(({ UserName, Hours, Date, Id }) => {
          return { userName: UserName, hours: Hours, date: Date, id: Id };
        })
      : [],
  );

  const dateIntervalText = computed(() => {
    const endDate = initialParams.toDate;
    const startDate = data.value
      ? data.value.pageParams[data.value.pageParams.length - 1].fromDate
      : initialParams.fromDate;

    return [date(startDate, { short: true }), date(endDate, { short: true })].join(' - ');
  });

  return {
    fetchNextPage,
    registrations: transformedRegistrations,
    isFetchingNextPage,
    isLoading,
    dateIntervalText,
  };
}
